import React, { useState, useEffect } from "react";
import { GlobalStyles } from "../../styles/globalStyles";
import {
  CreditCard,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Download,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Plus,
  DollarSign,
  Users,
  Eye,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Edit,
  Filter,
  ChevronDown,
  Sparkles,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
} from "lucide-react";
import toast from "react-hot-toast";
import { transactionService, adminService } from "../../services/api";
import { formatFiat, formatCrypto } from "../../utils/currency";
import { Transaction } from "../../types";

interface AdminTransaction extends Transaction {
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    username?: string;
  };
  admin_notes?:
    | string
    | {
        crypto_amount?: number;
        currency?: string;
        usd_amount?: number;
        network?: string;
        crypto_price?: number;
        description?: string;
        [key: string]: any;
      };
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

interface ApiResponse {
  current_page: number;
  data: any[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: Array<{
    url: string | null;
    label: string;
    active: boolean;
  }>;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

// Bonus Modal Component
const BonusModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: number, amount: number, reason: string) => Promise<void>;
  isLoading: boolean;
}> = ({ isOpen, onClose, onConfirm, isLoading }) => {
  const [userId, setUserId] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [userResults, setUserResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  useEffect(() => {
    if (!isOpen) {
      setUserId("");
      setAmount("");
      setReason("");
      setSearchTerm("");
      setUserResults([]);
      setSelectedUser(null);
    }
  }, [isOpen]);

  const handleSearchUsers = async () => {
    if (!searchTerm.trim()) return;

    setSearchLoading(true);
    try {
      const response = await adminService.getAllUsers();
      const users = Array.isArray(response.data) ? response.data : response;

      const filteredUsers = users
        .filter(
          (user: any) =>
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            `${user.first_name || ""} ${user.last_name || ""}`
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            user.id.toString().includes(searchTerm),
        )
        .slice(0, 10);

      setUserResults(filteredUsers);
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to search users");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleUserSelect = (user: any) => {
    setSelectedUser(user);
    setUserId(user.id.toString());
    setSearchTerm(`${user.first_name} ${user.last_name} (${user.email})`);
    setUserResults([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);
    const numUserId = parseInt(userId);

    if (
      !userId ||
      isNaN(numUserId) ||
      !amount ||
      isNaN(numAmount) ||
      numAmount <= 0 ||
      !reason.trim()
    ) {
      toast.error("Please fill in all fields with valid values");
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(numUserId, numAmount, reason);
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-200/30 dark:border-gray-700/30">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Add Bonus
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors backdrop-blur-sm disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* User Selection */}
            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Select User
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (e.target.value.trim()) {
                      handleSearchUsers();
                    }
                  }}
                  placeholder="Search by name, email, or ID..."
                  className={`${GlobalStyles.input} pl-10 w-full backdrop-blur-sm`}
                  disabled={isSubmitting || isLoading}
                />
                {searchLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  </div>
                )}
              </div>

              {userResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl border border-gray-200/30 dark:border-gray-700/30 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {userResults.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleUserSelect(user)}
                      className="w-full text-left p-3 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-colors border-b border-gray-100/50 dark:border-gray-700/50 last:border-0 group/user"
                    >
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center mr-3 transition-transform group-hover/user:scale-110">
                          <Users className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {user.first_name} {user.last_name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            ID: {user.id}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {selectedUser && (
                <div className="mt-2 p-3 bg-green-500/5 border border-green-200/50 dark:border-green-800/50 rounded-lg backdrop-blur-sm">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-700 dark:text-green-300">
                        Selected: {selectedUser.first_name}{" "}
                        {selectedUser.last_name}
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-400">
                        Balance: {formatFiat(selectedUser.balance || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Amount */}
            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Bonus Amount
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter bonus amount"
                  disabled={isSubmitting || isLoading}
                  className={`${GlobalStyles.input} pl-10 w-full backdrop-blur-sm`}
                  required
                />
              </div>
            </div>

            {/* Reason */}
            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Reason
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for bonus..."
                rows={3}
                disabled={isSubmitting || isLoading}
                className={`${GlobalStyles.input} w-full resize-none backdrop-blur-sm`}
                required
                maxLength={500}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {reason.length}/500 characters
              </p>
            </div>

            <div className="p-4 bg-blue-500/5 border border-blue-200/50 dark:border-blue-800/50 rounded-xl backdrop-blur-sm">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  This bonus will be automatically added to the user's balance
                  and will appear as a bonus transaction.
                </p>
              </div>
            </div>
          </form>

          <div className="flex items-center justify-end p-6 border-t border-gray-200/30 dark:border-gray-700/30 space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} backdrop-blur-sm disabled:opacity-50 group/cancel`}
            >
              <span>Cancel</span>
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={
                isSubmitting || isLoading || !userId || !amount || !reason
              }
              className={`${GlobalStyles.button} ${GlobalStyles.buttonSuccess} ${GlobalStyles.buttonMedium} min-w-24 backdrop-blur-sm disabled:opacity-50 group/submit`}
            >
              {isSubmitting || isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isSubmitting ? "Adding..." : "Loading..."}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <Plus className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-x-0.5" />
                  <span>Add Bonus</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Status Update Modal Component
const StatusUpdateModal: React.FC<{
  transaction: AdminTransaction | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    id: number,
    status: string,
    txHash?: string,
    adminNotes?: string,
  ) => Promise<void>;
  isLoading: boolean;
}> = ({ transaction, isOpen, onClose, onConfirm, isLoading }) => {
  const [status, setStatus] = useState<string>(
    transaction?.status || "pending",
  );
  const [txHash, setTxHash] = useState<string>(transaction?.tx_hash || "");
  const [adminNotes, setAdminNotes] = useState<string>(() => {
    if (transaction?.admin_notes) {
      if (typeof transaction.admin_notes === "string") {
        try {
          const parsed = JSON.parse(transaction.admin_notes);
          return typeof parsed === "object"
            ? JSON.stringify(parsed, null, 2)
            : transaction.admin_notes;
        } catch {
          return transaction.admin_notes;
        }
      } else if (typeof transaction.admin_notes === "object") {
        return JSON.stringify(transaction.admin_notes, null, 2);
      }
    }
    return "";
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (transaction) {
      setStatus(transaction.status || "pending");
      setTxHash(transaction.tx_hash || "");

      if (transaction.admin_notes) {
        if (typeof transaction.admin_notes === "string") {
          try {
            const parsed = JSON.parse(transaction.admin_notes);
            setAdminNotes(
              typeof parsed === "object"
                ? JSON.stringify(parsed, null, 2)
                : transaction.admin_notes,
            );
          } catch {
            setAdminNotes(transaction.admin_notes);
          }
        } else if (typeof transaction.admin_notes === "object") {
          setAdminNotes(JSON.stringify(transaction.admin_notes, null, 2));
        }
      } else {
        setAdminNotes("");
      }
    }
  }, [transaction]);

  if (!isOpen || !transaction) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let parsedNotes = adminNotes;
      try {
        if (adminNotes.trim().startsWith("{")) {
          parsedNotes = JSON.stringify(JSON.parse(adminNotes));
        }
      } catch (error) {
        // If not valid JSON, keep as string
      }

      await onConfirm(
        transaction.id,
        status,
        txHash || undefined,
        parsedNotes || undefined,
      );
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTransactionDetails = () => {
    if (!transaction.admin_notes) return null;

    try {
      if (typeof transaction.admin_notes === "string") {
        const parsed = JSON.parse(transaction.admin_notes);
        return {
          crypto_amount: parsed.crypto_amount,
          currency: parsed.currency,
          usd_amount: parsed.usd_amount,
          network: parsed.network,
          crypto_price: parsed.crypto_price,
          description: parsed.description,
        };
      } else if (typeof transaction.admin_notes === "object") {
        return transaction.admin_notes;
      }
    } catch (error) {
      console.error("Error parsing admin notes:", error);
    }
    return null;
  };

  const details = getTransactionDetails();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 w-full max-w-lg">
          <div className="flex items-center justify-between p-6 border-b border-gray-200/30 dark:border-gray-700/30">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Update Transaction Status
            </h3>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors backdrop-blur-sm disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Transaction Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">User</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {transaction.user?.first_name} {transaction.user?.last_name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {transaction.user?.email}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Type</p>
                <span
                  className={`${GlobalStyles.badge} ${
                    transaction.type === "deposit"
                      ? GlobalStyles.badgeSuccess
                      : transaction.type === "withdrawal"
                        ? GlobalStyles.badgeError
                        : "bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50"
                  }`}
                >
                  {transaction.type}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Amount
                </p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatFiat(transaction.amount)}
                </p>
                {details && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {details.crypto_amount &&
                      formatCrypto(
                        details.crypto_amount,
                        details.currency || "USD",
                      )}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Current Status
                </p>
                <span
                  className={`${GlobalStyles.badge} ${
                    transaction.status === "confirmed"
                      ? GlobalStyles.badgeSuccess
                      : transaction.status === "pending"
                        ? GlobalStyles.badgeWarning
                        : GlobalStyles.badgeError
                  }`}
                >
                  {transaction.status}
                </span>
              </div>
            </div>

            {/* Status Selection */}
            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                New Status
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {["pending", "confirmed", "rejected", "cancelled"].map((s) => (
                  <label
                    key={s}
                    className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all duration-200 group/status ${
                      status === s
                        ? "border-primary bg-primary/10"
                        : "border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50/50 dark:hover:bg-gray-700/50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={s}
                      checked={status === s}
                      onChange={(e) => setStatus(e.target.value)}
                      disabled={isSubmitting || isLoading}
                      className="sr-only"
                    />
                    <div className="flex flex-col items-center">
                      {s === "confirmed" && (
                        <CheckCircle className="h-5 w-5 text-green-500 mb-1 group-hover/status:scale-110 transition-transform" />
                      )}
                      {s === "pending" && (
                        <Clock className="h-5 w-5 text-yellow-500 mb-1 group-hover/status:scale-110 transition-transform" />
                      )}
                      {s === "rejected" && (
                        <XCircle className="h-5 w-5 text-red-500 mb-1 group-hover/status:scale-110 transition-transform" />
                      )}
                      {s === "cancelled" && (
                        <X className="h-5 w-5 text-gray-500 mb-1 group-hover/status:scale-110 transition-transform" />
                      )}
                      <span className="text-sm capitalize">{s}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Transaction Hash (for withdrawals) */}
            {transaction.type === "withdrawal" && (
              <div>
                <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                  Transaction Hash
                </label>
                <input
                  type="text"
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  placeholder="Enter blockchain transaction hash"
                  disabled={isSubmitting || isLoading}
                  className={`${GlobalStyles.input} backdrop-blur-sm`}
                />
              </div>
            )}

            {/* Admin Notes */}
            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Admin Notes
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Enter admin notes..."
                rows={4}
                disabled={isSubmitting || isLoading}
                className={`${GlobalStyles.input} w-full resize-none backdrop-blur-sm`}
              />
            </div>

            {/* Details */}
            {details && (
              <div className="p-4 bg-gray-500/5 border border-gray-200/50 dark:border-gray-700/50 rounded-xl backdrop-blur-sm">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Transaction Details:
                </p>
                <div className="text-sm space-y-1">
                  {details.crypto_amount && (
                    <p>
                      Cryptocurrency:{" "}
                      {formatCrypto(
                        details.crypto_amount,
                        details.currency || "USD",
                      )}
                    </p>
                  )}
                  {details.usd_amount && (
                    <p>USD Amount: {formatFiat(details.usd_amount)}</p>
                  )}
                  {details.network && <p>Network: {details.network}</p>}
                  {details.crypto_price && <p>Rate: ${details.crypto_price}</p>}
                  {details.description && (
                    <p>Description: {details.description}</p>
                  )}
                </div>
              </div>
            )}
          </form>

          <div className="flex items-center justify-end p-6 border-t border-gray-200/30 dark:border-gray-700/30 space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} backdrop-blur-sm disabled:opacity-50`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || isLoading}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} min-w-24 backdrop-blur-sm disabled:opacity-50 group/submit`}
            >
              {isSubmitting || isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isSubmitting ? "Updating..." : "Loading..."}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-x-0.5" />
                  <span>Update Status</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminTransactions: React.FC = () => {
  const [transactions, setTransactions] = useState<AdminTransaction[]>([]);
  const [filter, setFilter] = useState({
    type: "",
    status: "",
    search: "",
    user_id: "",
    start_date: "",
    end_date: "",
  });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationData>({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
    from: 1,
    to: 1,
  });
  const [selectedTransaction, setSelectedTransaction] =
    useState<AdminTransaction | null>(null);
  const [bonusModalOpen, setBonusModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [updatingTransactionId, setUpdatingTransactionId] = useState<
    number | null
  >(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [statistics, setStatistics] = useState({
    total_deposits: 0,
    total_withdrawals: 0,
    total_bonuses: 0,
    pending_deposits: 0,
    pending_withdrawals: 0,
    net_flow: 0,
    total_transactions: 0,
    unique_users: 0,
  });

  useEffect(() => {
    fetchTransactions();
    fetchStatistics();
  }, [filter.type, filter.status, filter.user_id, pagination.current_page]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await transactionService.getAllTransactions({
        type: filter.type || undefined,
        status: filter.status || undefined,
        user_id: filter.user_id || undefined,
        start_date: filter.start_date || undefined,
        end_date: filter.end_date || undefined,
        search: filter.search || undefined,
        page: pagination.current_page,
        per_page: pagination.per_page,
      });

      let apiData: ApiResponse;
      if (response.data) {
        apiData = response;
      } else if (Array.isArray(response)) {
        apiData = {
          data: response,
          current_page: 1,
          last_page: 1,
          per_page: response.length,
          total: response.length,
          from: 1,
          to: response.length,
          first_page_url: "",
          last_page_url: "",
          links: [],
          next_page_url: null,
          path: "",
          prev_page_url: null,
        };
      } else {
        apiData = response;
      }

      const transformedTransactions = (apiData.data || []).map((item: any) => ({
        id: item.id,
        type: item.type,
        amount: parseFloat(item.amount) || 0,
        status: item.status,
        tx_hash: item.tx_hash,
        wallet_address: item.wallet_address,
        admin_notes: item.admin_notes,
        created_at: item.created_at,
        updated_at: item.updated_at,
        user: item.user
          ? {
              id: item.user.id,
              first_name: item.user.first_name,
              last_name: item.user.last_name,
              email: item.user.email,
              username: item.user.username,
            }
          : undefined,
      }));

      setTransactions(transformedTransactions);
      setPagination({
        current_page: apiData.current_page || 1,
        last_page: apiData.last_page || 1,
        per_page: apiData.per_page || 20,
        total: apiData.total || 0,
        from: apiData.from || 1,
        to: apiData.to || 1,
      });

      setSelectedIds([]);
    } catch (error: any) {
      console.error("Failed to fetch transactions:", error);
      toast.error(error.response?.data?.error || "Failed to load transactions");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await transactionService.getTransactionStatisticsAdmin({
        start_date: filter.start_date || undefined,
        end_date: filter.end_date || undefined,
      });

      if (response.overview) {
        setStatistics({
          total_deposits: response.overview.total_deposits || 0,
          total_withdrawals: response.overview.total_withdrawals || 0,
          total_bonuses: response.overview.total_bonuses || 0,
          pending_deposits: response.overview.pending_deposits_count || 0,
          pending_withdrawals: response.overview.pending_withdrawals_count || 0,
          net_flow: response.overview.net_flow || 0,
          total_transactions: response.overview.total_transactions || 0,
          unique_users: response.overview.unique_users || 0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch statistics:", error);
    }
  };

  const filteredTransactions = transactions.filter((transaction) => {
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      const matches =
        transaction.id.toString().includes(searchLower) ||
        transaction.tx_hash?.toLowerCase().includes(searchLower) ||
        transaction.wallet_address?.toLowerCase().includes(searchLower) ||
        transaction.user?.email.toLowerCase().includes(searchLower) ||
        transaction.user?.first_name.toLowerCase().includes(searchLower) ||
        transaction.user?.last_name.toLowerCase().includes(searchLower);

      if (!matches) {
        if (transaction.admin_notes) {
          try {
            const notesStr =
              typeof transaction.admin_notes === "string"
                ? transaction.admin_notes
                : JSON.stringify(transaction.admin_notes);
            if (notesStr.toLowerCase().includes(searchLower)) {
              return true;
            }
          } catch (error) {
            console.error("Error searching admin notes:", error);
          }
        }
        return false;
      }
    }
    return true;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "confirmed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "cancelled":
        return <X className="h-4 w-4 text-gray-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTransactionDetails = (transaction: AdminTransaction) => {
    if (!transaction.admin_notes) return null;

    try {
      if (typeof transaction.admin_notes === "string") {
        const parsed = JSON.parse(transaction.admin_notes);
        return {
          crypto_amount: parsed.crypto_amount,
          currency: parsed.currency,
          usd_amount: parsed.usd_amount,
          network: parsed.network,
          crypto_price: parsed.crypto_price,
          description: parsed.description,
        };
      } else if (typeof transaction.admin_notes === "object") {
        return transaction.admin_notes;
      }
    } catch (error) {
      console.error("Error parsing admin notes:", error);
    }
    return null;
  };

  const handleAddBonus = async (
    userId: number,
    amount: number,
    reason: string,
  ) => {
    try {
      await transactionService.addBonus({
        user_id: userId,
        amount: amount,
        reason: reason,
      });

      toast.success(`Added ${formatFiat(amount)} bonus to user`);
      fetchTransactions();
      fetchStatistics();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to add bonus");
      throw error;
    }
  };

  const handleStatusUpdate = async (
    id: number,
    status: string,
    txHash?: string,
    adminNotes?: string,
  ) => {
    setUpdatingTransactionId(id);

    try {
      await transactionService.updateTransactionStatus(id, {
        status: status,
        tx_hash: txHash || undefined,
        admin_notes: adminNotes || undefined,
      });

      toast.success(`Transaction status updated to ${status}`);
      fetchTransactions();
      fetchStatistics();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to update status");
      throw error;
    } finally {
      setUpdatingTransactionId(null);
    }
  };

  const handleBulkStatusUpdate = async (status: string) => {
    if (selectedIds.length === 0) {
      toast.error("Please select transactions first");
      return;
    }

    if (
      !window.confirm(
        `Update ${selectedIds.length} transaction(s) to ${status}?`,
      )
    ) {
      return;
    }

    setBulkUpdating(true);
    try {
      await transactionService.bulkUpdateTransactions({
        transaction_ids: selectedIds,
        status: status,
        admin_notes: "Bulk update by admin",
      });

      toast.success(
        `Updated ${selectedIds.length} transaction(s) to ${status}`,
      );
      setSelectedIds([]);
      fetchTransactions();
      fetchStatistics();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to bulk update");
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await transactionService.exportTransactions({
        start_date:
          filter.start_date ||
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0],
        end_date: filter.end_date || new Date().toISOString().split("T")[0],
        format: "csv",
        type: filter.type || undefined,
        status: filter.status || undefined,
      });

      if (response.data) {
        const jsonStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([jsonStr], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transactions_export_${
          new Date().toISOString().split("T")[0]
        }.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const blob = new Blob([response], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transactions_export_${
          new Date().toISOString().split("T")[0]
        }.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      toast.success("Export started");
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to export");
    }
  };

  const handleSelectTransaction = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((transactionId) => transactionId !== id)
        : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredTransactions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredTransactions.map((t) => t.id));
    }
  };

  const openStatusModal = (transaction: AdminTransaction) => {
    setSelectedTransaction(transaction);
    setStatusModalOpen(true);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.last_page) {
      setPagination((prev) => ({ ...prev, current_page: page }));
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      pagination.current_page - Math.floor(maxVisiblePages / 2),
    );
    let endPage = Math.min(
      pagination.last_page,
      startPage + maxVisiblePages - 1,
    );

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 backdrop-blur-sm ${
            pagination.current_page === i
              ? "bg-gradient-to-r from-primary to-primary/90 text-white shadow-sm"
              : "bg-white/50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 border border-gray-200/50 dark:border-gray-700/50"
          }`}
        >
          {i}
        </button>,
      );
    }

    return pages;
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
            <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 p-12">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin mx-auto"></div>
                <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
              </div>
              <p className="mt-6 text-gray-600 dark:text-gray-400 font-medium">
                Loading transactions...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      <div className="p-4 lg:p-5">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Transaction Management
            </h1>
            <Sparkles className="h-4 w-4 text-primary/60" />
          </div>
          <p className="text-gray-500 dark:text-gray-400">
            Manage all deposits, withdrawals, and bonuses
          </p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-green-100/50 to-green-50/50 dark:from-green-900/20 dark:to-green-800/10">
                <ArrowDownRight className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess} px-2 py-0.5`}
              >
                Deposits
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Deposits
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {formatFiat(statistics.total_deposits)}
            </p>
            <div className="flex items-center mt-4 pt-3 border-t border-gray-100/50 dark:border-gray-700/50">
              <Activity className="h-3 w-3 text-green-500 mr-1" />
              <span className="text-xs text-green-600 dark:text-green-400">
                {statistics.pending_deposits} pending
              </span>
            </div>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-red-100/50 to-red-50/50 dark:from-red-900/20 dark:to-red-800/10">
                <ArrowUpRight className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeError} px-2 py-0.5`}
              >
                Withdrawals
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Withdrawals
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {formatFiat(statistics.total_withdrawals)}
            </p>
            <div className="flex items-center mt-4 pt-3 border-t border-gray-100/50 dark:border-gray-700/50">
              <Activity className="h-3 w-3 text-red-500 mr-1" />
              <span className="text-xs text-red-600 dark:text-red-400">
                {statistics.pending_withdrawals} pending
              </span>
            </div>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-100/50 to-purple-50/50 dark:from-purple-900/20 dark:to-purple-800/10">
                <DollarSign className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgePrimary} px-2 py-0.5`}
              >
                Bonuses
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Bonuses
            </p>
            <p className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mt-1">
              {formatFiat(statistics.total_bonuses)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Added to user balances
            </p>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100/50 to-blue-50/50 dark:from-blue-900/20 dark:to-blue-800/10">
                <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <span
                className={`${GlobalStyles.badge} ${GlobalStyles.badgeInfo} px-2 py-0.5`}
              >
                Net Flow
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Platform Net Flow
            </p>
            <p
              className={`text-xl font-bold ${
                statistics.net_flow >= 0
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              } mt-1`}
            >
              {statistics.net_flow >= 0 ? "+" : ""}
              {formatFiat(statistics.net_flow)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {statistics.total_transactions} total transactions
            </p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 p-5 shadow-lg mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Filter Transactions
              </h3>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setBonusModalOpen(true)}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonSuccess} ${GlobalStyles.buttonMedium} backdrop-blur-sm group/bonus`}
              >
                <Plus className="h-4 w-4 mr-2 transition-transform group-hover/bonus:-translate-y-0.5" />
                Add Bonus
              </button>
              <button
                onClick={handleExport}
                disabled={transactions.length === 0}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} backdrop-blur-sm disabled:opacity-50 group/export`}
              >
                <Download className="h-4 w-4 mr-2 transition-transform group-hover/export:-translate-y-0.5" />
                Export
              </button>
              <button
                onClick={fetchTransactions}
                disabled={loading}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} backdrop-blur-sm disabled:opacity-50 group/refresh`}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2 transition-transform group-hover/refresh:rotate-180" />
                )}
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative group">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <CreditCard className="h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              </div>
              <select
                value={filter.type}
                onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                className={`${GlobalStyles.input} pl-10 appearance-none pr-8 backdrop-blur-sm`}
              >
                <option value="">All Types</option>
                <option value="deposit">Deposit</option>
                <option value="withdrawal">Withdrawal</option>
                <option value="bonus">Bonus</option>
                <option value="adjustment">Adjustment</option>
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>

            <div className="relative group">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <FileText className="h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              </div>
              <select
                value={filter.status}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value })
                }
                className={`${GlobalStyles.input} pl-10 appearance-none pr-8 backdrop-blur-sm`}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>

            <div className="relative group">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <Clock className="h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              </div>
              <input
                type="date"
                value={filter.start_date}
                onChange={(e) =>
                  setFilter({ ...filter, start_date: e.target.value })
                }
                className={`${GlobalStyles.input} pl-10 backdrop-blur-sm`}
              />
            </div>

            <div className="relative group">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <Clock className="h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              </div>
              <input
                type="date"
                value={filter.end_date}
                onChange={(e) =>
                  setFilter({ ...filter, end_date: e.target.value })
                }
                className={`${GlobalStyles.input} pl-10 backdrop-blur-sm`}
              />
            </div>

            <div className="relative group">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              <input
                type="text"
                placeholder="Search by ID, hash, email..."
                value={filter.search}
                onChange={(e) =>
                  setFilter({ ...filter, search: e.target.value })
                }
                className={`${GlobalStyles.input} pl-10 backdrop-blur-sm`}
              />
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedIds.length > 0 && (
            <div className="mt-4 p-4 bg-gradient-to-r from-yellow-500/5 to-orange-500/5 border border-yellow-200/50 dark:border-yellow-800/50 rounded-xl backdrop-blur-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0" />
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {selectedIds.length} transaction(s) selected
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => handleBulkStatusUpdate("confirmed")}
                    disabled={bulkUpdating}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonSuccess} ${GlobalStyles.buttonSmall} backdrop-blur-sm disabled:opacity-50 group/confirm`}
                  >
                    {bulkUpdating ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <CheckCircle className="h-3 w-3 mr-1 transition-transform group-hover/confirm:-translate-y-0.5" />
                    )}
                    Confirm Selected
                  </button>
                  <button
                    onClick={() => handleBulkStatusUpdate("rejected")}
                    disabled={bulkUpdating}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonError} ${GlobalStyles.buttonSmall} backdrop-blur-sm disabled:opacity-50 group/reject`}
                  >
                    {bulkUpdating ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <XCircle className="h-3 w-3 mr-1 transition-transform group-hover/reject:-translate-y-0.5" />
                    )}
                    Reject Selected
                  </button>
                  <button
                    onClick={() => handleBulkStatusUpdate("cancelled")}
                    disabled={bulkUpdating}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} backdrop-blur-sm disabled:opacity-50 group/cancel`}
                  >
                    {bulkUpdating ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <X className="h-3 w-3 mr-1 transition-transform group-hover/cancel:-translate-y-0.5" />
                    )}
                    Cancel Selected
                  </button>
                  <button
                    onClick={() => setSelectedIds([])}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall}`}
                  >
                    Clear Selection
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-gray-200/30 dark:border-gray-700/30 mt-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-primary animate-pulse"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {filteredTransactions.length}
                </span>{" "}
                transactions match your filters
              </p>
            </div>
            <button
              onClick={() => {
                setFilter({
                  type: "",
                  status: "",
                  search: "",
                  user_id: "",
                  start_date: "",
                  end_date: "",
                });
              }}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} group/clear`}
            >
              <span>Clear Filters</span>
            </button>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl border border-gray-200/30 dark:border-gray-700/30 shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-900/50 backdrop-blur-sm">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider w-10">
                    <input
                      type="checkbox"
                      checked={
                        selectedIds.length === filteredTransactions.length &&
                        filteredTransactions.length > 0
                      }
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-primary rounded focus:ring-primary border-gray-300"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Transaction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200/30 dark:divide-gray-700/30">
                {filteredTransactions.length > 0 ? (
                  filteredTransactions.map((transaction) => {
                    const details = getTransactionDetails(transaction);
                    const isSelected = selectedIds.includes(transaction.id);

                    return (
                      <tr
                        key={transaction.id}
                        className={`hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-all duration-200 group/row ${
                          isSelected ? "bg-primary/5 dark:bg-primary/10" : ""
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              handleSelectTransaction(transaction.id)
                            }
                            className="h-4 w-4 text-primary rounded focus:ring-primary border-gray-300"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-mono text-sm text-gray-900 dark:text-white">
                            #{transaction.id}
                          </div>
                          {transaction.tx_hash && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[120px]">
                              {transaction.tx_hash.substring(0, 16)}...
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {transaction.user ? (
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {transaction.user.first_name}{" "}
                                {transaction.user.last_name}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {transaction.user.email}
                              </p>
                              {transaction.user.username && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  @{transaction.user.username}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-500 dark:text-gray-400">
                              User not found
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`${GlobalStyles.badge} ${
                              transaction.type === "deposit"
                                ? GlobalStyles.badgeSuccess
                                : transaction.type === "withdrawal"
                                  ? GlobalStyles.badgeError
                                  : "bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50"
                            }`}
                          >
                            {transaction.type}
                          </span>
                          {details && details.currency && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {details.currency}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`font-medium ${
                              transaction.type === "deposit"
                                ? "text-green-600 dark:text-green-400"
                                : transaction.type === "withdrawal"
                                  ? "text-red-600 dark:text-red-400"
                                  : "text-purple-600 dark:text-purple-400"
                            }`}
                          >
                            {transaction.type === "deposit"
                              ? "+"
                              : transaction.type === "withdrawal"
                                ? "-"
                                : ""}
                            {formatFiat(transaction.amount)}
                          </div>
                          {details && details.crypto_amount && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {formatCrypto(
                                details.crypto_amount,
                                details.currency || "USD",
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStatusIcon(transaction.status)}
                            <span
                              className={`${GlobalStyles.badge} ml-2 ${
                                transaction.status === "confirmed"
                                  ? GlobalStyles.badgeSuccess
                                  : transaction.status === "pending"
                                    ? GlobalStyles.badgeWarning
                                    : GlobalStyles.badgeError
                              }`}
                            >
                              {transaction.status}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {new Date(
                                transaction.created_at,
                              ).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(
                                transaction.created_at,
                              ).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => openStatusModal(transaction)}
                              disabled={
                                updatingTransactionId === transaction.id
                              }
                              className="p-2 rounded-lg hover:bg-primary/10 transition-colors disabled:opacity-50 group/update"
                              title="Update Status"
                            >
                              {updatingTransactionId === transaction.id ? (
                                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                              ) : (
                                <Edit className="h-4 w-4 text-gray-500 dark:text-gray-400 group-hover/update:scale-110 transition-transform" />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                const details =
                                  getTransactionDetails(transaction);
                                if (details) {
                                  toast.success(`Transaction details loaded`);
                                }
                              }}
                              className="p-2 rounded-lg hover:bg-blue-500/10 transition-colors group/view"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4 text-gray-500 dark:text-gray-400 group-hover/view:scale-110 transition-transform" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-12">
                      <div className="text-center">
                        <div className="relative mx-auto w-20 h-20 mb-4">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-full blur-lg"></div>
                          <div className="relative w-20 h-20 bg-white/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30">
                            <CreditCard className="h-8 w-8 text-gray-400" />
                          </div>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          No transactions found
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                          {transactions.length === 0
                            ? "No transactions yet"
                            : "Try adjusting your filters or search terms"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredTransactions.length > 0 &&
            pagination.total > pagination.per_page && (
              <div className="flex flex-col md:flex-row items-center justify-between px-6 py-4 border-t border-gray-200/30 dark:border-gray-700/30 gap-4 backdrop-blur-sm">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing{" "}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {pagination.from}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {pagination.to}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {pagination.total}
                  </span>{" "}
                  transactions
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      handlePageChange(pagination.current_page - 1)
                    }
                    disabled={pagination.current_page === 1}
                    className="p-2 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed group/prev"
                    title="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4 transition-transform group-hover/prev:-translate-x-0.5" />
                  </button>

                  {renderPagination()}

                  <button
                    onClick={() =>
                      handlePageChange(pagination.current_page + 1)
                    }
                    disabled={pagination.current_page === pagination.last_page}
                    className="p-2 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed group/next"
                    title="Next page"
                  >
                    <ChevronRight className="h-4 w-4 transition-transform group-hover/next:translate-x-0.5" />
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Modals */}
      <BonusModal
        isOpen={bonusModalOpen}
        onClose={() => setBonusModalOpen(false)}
        onConfirm={handleAddBonus}
        isLoading={false}
      />

      <StatusUpdateModal
        transaction={selectedTransaction}
        isOpen={statusModalOpen}
        onClose={() => {
          setStatusModalOpen(false);
          setSelectedTransaction(null);
        }}
        onConfirm={handleStatusUpdate}
        isLoading={updatingTransactionId === selectedTransaction?.id}
      />
    </div>
  );
};

export default AdminTransactions;
